// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "defaultName": "Dinero", "path": "dinero.js" }

import Dinero from 'dinero.js';
import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';

export class Currency {
  readonly number: string;
  readonly currency: string;

  constructor(data: { [key: string]: any } = {}) {
    this.number = data.number === undefined ? '0.0000' : data.number;
    this.currency = data.currency === undefined ? 'USD' : data.currency;
  }

  static zero(): Currency {
    const zeroValues: ExcludeMethodsDeep<Currency> = {
      number: '0.0000',
      currency: 'USD',
    };
    return new Currency(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /**
   * Converts a Dinero back into a Currency.
   *
   * `asDinero` doesn't carry the currency across, so every Dinero here reads back as Dinero's own
   * default. Arithmetic passes the operands' currency explicitly to keep it off the result.
   */
  static fromDinero(dinero: Dinero.Dinero, currency?: string): Currency {
    const precision = Math.min(dinero.getPrecision(), 20);
    const number = dinero.toUnit().toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
      useGrouping: false,
    });
    return new Currency({ number, currency: currency ?? dinero.getCurrency() });
  }

  /**
   * Returns the sum of this Currency with the provided values.
   *
   * For now, all of the Currency objects being added must have the same currency (e.g. they could
   * all be "USD"), except that any zero-quantity value -- this Currency included -- can use a
   * mismatched currency.
   */
  add(...values: Currency[]): Currency | null {
    const currencies = [this, ...values]
      .filter((v) => Number(v.number) !== 0)
      .map((v) => v.currency);
    if (new Set(currencies).size > 1) {
      // TODO handle mismatched currency
      return null;
    }

    const dineros = [this, ...values]
      .filter((value) => Number.isFinite(Number(value.number)))
      .map((item) => (item instanceof Currency ? item : new Currency(item)).asDinero());
    const total = dineros.reduce((sum, value) => sum.add(value), Dinero({ amount: 0 }));
    return Currency.fromDinero(total, currencies[0] ?? this.currency);
  }

  /**
   * Returns the difference of this Currency with the provided values.
   *
   * For now, all of the Currency objects being subtracted must have the same currency (e.g. they could
   * all be "USD"), except that any zero-quantity value -- this Currency included -- can use a
   * mismatched currency.
   */
  subtract(...values: Currency[]): Currency | null {
    const currencies = [this, ...values]
      .filter((v) => Number(v.number) !== 0)
      .map((v) => v.currency);
    if (new Set(currencies).size > 1) {
      // TODO handle mismatched currency
      return null;
    }

    const dineros = values.map((item) =>
      (item instanceof Currency ? item : new Currency(item)).asDinero(),
    );
    const total = dineros.reduce((sum, value) => sum.subtract(value), this.asDinero());
    return Currency.fromDinero(total, currencies[0] ?? this.currency);
  }

  /**
   * Returns a new Currency whose number consists of this Currency's number multiplied by
   * the specified factor.
   */
  multiply(factor: number): Currency | null {
    if (!Number.isFinite(factor)) {
      // factor is NaN or +/- Infinity
      return null;
    }
    let dinero = this.asDinero();
    // When performing multiplication, Dinero.js rounds the result to the existing precision. If the existing
    // precision is less granular than 4 decimal places, increase precision to ensure accurate result.
    if (dinero.getPrecision() < 4) {
      dinero = dinero.convertPrecision(4);
    }
    return Currency.fromDinero(dinero.multiply(factor), this.currency);
  }

  /**
   * Returns a new Currency whose number consists of this Currency's number divided by
   * the specified denominator.
   */
  divide(denominator: number): Currency | null {
    if (denominator === 0 || Number.isNaN(denominator)) {
      // denominator is zero or NaN
      return null;
    }
    let dinero = this.asDinero();
    // When performing division, Dinero.js rounds the result to the existing precision. If the existing
    // precision is less granular than 4 decimal places, increase precision to ensure accurate result.
    if (dinero.getPrecision() < 4) {
      dinero = dinero.convertPrecision(4);
    }
    return Currency.fromDinero(dinero.divide(denominator), this.currency);
  }

  asDinero() {
    const cleanedNumber = this.number === '' ? '0' : this.number.replaceAll(',', '');
    if (!Number.isFinite(Number(cleanedNumber))) {
      return Dinero({ amount: 0, precision: 0 });
    }

    const precision =
      cleanedNumber.indexOf('.') === -1 ? 0 : cleanedNumber.length - 1 - cleanedNumber.indexOf('.');

    const amount = parseInt(cleanedNumber.replace('.', ''), 10);
    const safeAmount = Number.isFinite(amount) ? amount : 0;

    return Dinero({ amount: safeAmount, precision });
  }

  /** Returns this Currency's value as a plain number, e.g. for arithmetic that doesn't need Currency's precision handling. */
  toUnit(): number {
    return this.asDinero().toUnit();
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type CurrencyWireFormat = {
  number: string;
  currency: string;
};

type NewCurrencyProps = PartialDeep<Currency, { recurseIntoArrays: true }>;
export const NewCurrency: (props: NewCurrencyProps) => Currency = (props) => new Currency(props);

type NewCurrencyFromWireFormatProps = PartialDeep<CurrencyWireFormat, { recurseIntoArrays: true }>;
export const NewCurrencyFromWireFormat: (props: NewCurrencyFromWireFormatProps) => Currency = (
  props,
) => new Currency(props);
